import React, { ReactNode } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link, useLocation, useNavigate } from '../../router/Router';
import {
  IconBuilding,
  IconFolder,
  IconSettings,
  IconShield,
  IconSync,
  IconUsers,
} from '../ui/Icons';
import { Badge } from '../ui/Badge';

const ADMIN_NAV_ITEMS = [
  { to: '/admin/companies', label: 'Companies & Billing', icon: IconBuilding },
  { to: '/admin/users', label: 'Users & Login-As', icon: IconUsers },
  { to: '/admin/projects', label: 'Projects & Pipes Explorer', icon: IconFolder },
  { to: '/admin/admins', label: 'Platform Admins', icon: IconShield },
  { to: '/admin/maintenance', label: 'System & Queue', icon: IconSettings },
];

export function AdminLayout({ children }: { children: ReactNode }) {
  const { portalMode, maintenanceMode, logoutAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleAdminLogout = async () => {
    await logoutAdmin();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-100 text-slate-900">
      {/* Top Admin Header */}
      <header className="sticky top-0 z-30 border-b border-slate-800 bg-slate-900 text-white shadow">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8 h-16">
          <div className="flex items-center gap-6">
            <Link to="/admin/companies" className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-500 text-white shadow-sm">
                <IconShield className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-base font-bold tracking-tight text-white">Sync Admin</span>
                  <span className="rounded bg-indigo-500/20 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-indigo-300 border border-indigo-400/30">
                    Operations Console
                  </span>
                </div>
                <p className="text-[11px] text-slate-400 hidden sm:block">
                  Multi-Tenant Administration &amp; Queue Control
                </p>
              </div>
            </Link>

            <nav aria-label="Admin console navigation" className="hidden lg:flex items-center gap-1">
              {ADMIN_NAV_ITEMS.map((item) => {
                const Icon = item.icon;
                const active =
                  location.pathname === item.to ||
                  location.pathname.startsWith(`${item.to}/`) ||
                  (item.to === '/admin/maintenance' &&
                    location.pathname.startsWith('/admin/settings'));

                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium transition-colors ${
                      active
                        ? 'bg-indigo-600 text-white'
                        : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </nav>
          </div>

          <div className="flex items-center gap-3">
            {maintenanceMode ? (
              <Badge variant="danger" dot>
                Maintenance ON
              </Badge>
            ) : (
              <Badge variant="success" dot>
                Workers Healthy
              </Badge>
            )}

            {portalMode === 'all' && (
              <Link
                to="/projects"
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-xs font-semibold text-slate-200 hover:bg-slate-700 transition-colors"
              >
                <IconSync className="w-3.5 h-3.5 text-indigo-400" />
                <span>Client Portal</span>
              </Link>
            )}

            <button
              type="button"
              onClick={handleAdminLogout}
              className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs font-medium text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>

        {/* Mobile/Tablet Navigation Bar */}
        <nav
          aria-label="Mobile admin navigation"
          className="flex lg:hidden items-center gap-1 overflow-x-auto border-t border-slate-800 px-4 py-2"
        >
          {ADMIN_NAV_ITEMS.map((item) => {
            const active =
              location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium ${
                  active ? 'bg-indigo-600 text-white' : 'text-slate-300 hover:bg-slate-800'
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>
      </header>

      {/* Main Content Landmark */}
      <main className="mx-auto w-full max-w-7xl flex-1 px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
}
